import React, { Fragment, useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.css";
import "./css/CartaReceta.css";
import Modal from "../secondary/Modal";
import ImageCarousel from "./ImageCarousel";
import { faHeart as faHeartSolid, faList, faThLarge, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { useDispatch } from "react-redux";
import { hideLoading, showLoading } from "../../../redux/actions/loadingActions";
import { mostrarError, mostrarExito } from "../../utils/Alertas";
import { db } from "../../firebase";

interface CartaRecetaProps {
    id: string;
    imagenUrl: string;
    nombre: string;
    ingredientes: string[];
    categoria: string;
    apuntes?: string;
    isGridView?: boolean;
    isEditing?: boolean;
    isFavorite: boolean;
    images?: string[];
    startIndex?: number;
    onIndexChange?: (i: number) => void;
    onEditarReceta: (receta: {
        id: string;
        nombre: string;
        imagenUrl: string;
        categoria: string;
        ingredientes: string[];
        apuntes?: string;
        isFavorite: boolean;
    }) => void;
    onDeleteReceta: (id: string) => void;
    onFavoriteToggle: (id: string) => void;
}

const CartaReceta: React.FC<CartaRecetaProps> = ({ id, imagenUrl, nombre, ingredientes, categoria, apuntes,
    isGridView = false, isEditing = false, isFavorite: initialFavorite, images, startIndex, onIndexChange, onEditarReceta, onDeleteReceta, onFavoriteToggle }) => {
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    // const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isFavorite, setIsFavorite] = useState(initialFavorite);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [ingredientesEnColumna, setIngredientesEnColumna] = useState(true);
    const [currentIndex, setCurrentIndex] = useState<number>(startIndex ?? 0);

    useEffect(() => {
    setCurrentIndex(startIndex ?? 0);
    }, [startIndex]);

    // Normaliza el array de imágenes (fallback a imagenUrl)
    const imageList = (images && images.length ? images : (imagenUrl ? [imagenUrl] : []));

    useEffect(() => {
        // Comprueba si la receta ya está marcada como favorita
        const fetchFavoriteStatus = async () => {
            const docRef = doc(db, "recetas", id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setIsFavorite(docSnap.data().isFavorite || false);
            }
        };

        fetchFavoriteStatus();
    }, [id]);

    useEffect(() => {
        if (!isModalOpen) {
            return;
        }

        const scrollY = window.scrollY;

        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = "0";
        document.body.style.right = "0";
        document.body.style.width = "100%";

        return () => {
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.left = "";
            document.body.style.right = "";
            document.body.style.width = "";

            window.scrollTo(0, scrollY);
        };
    }, [isModalOpen]);

    const handleShowModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    // const handleDelete = async (event: React.MouseEvent<SVGSVGElement>) => {
    //     event.stopPropagation();
    //     setIsConfirmModalOpen(true);
    // };

    // const cancelDeleteReceta = () => {
    //     setIsConfirmModalOpen(false);
    // };

    const getBadgeColor = (categoria: string): string => {
        const colores: { [key: string]: string } = {
            "Pasta": "#f4a261",
            "Pizza": "#e76f51",
            "Pescado": "#00b4d8",
            "Carnes": "#b56576",
            "Mariscos": "#2a9d8f",
            "Verduras": "#81b29a",
            "Legumbres": "#6a994e",
            "Hamburguesas": "#ef476f",
            "Arroz": "#ffd166",
            "Sopas": "#f5cac3",
            "Ensaladas": "#a7c957",
            "Postres": "#ffb4a2",
            "Repostería": "#ff9f1c",
            "Aperitivos": "#c77dff",
            "Sandwiches": "#ff6d00",
            "Desayunos": "#f7b801",
            "Smoothies": "#9b5de5",
            "Comida Internacional": "#06d6a0",
            "Otros": "#adb5bd",
        };

        return colores[categoria] || "#cccccc";
    };

    // const confirmDeleteReceta = async () => {
    //     if (id) {
    //         dispatch(showLoading());
    //         try {
    //             await deleteDoc(doc(db, "recetas", id));
    //             onDeleteReceta(id);
    //             mostrarExito(`La receta "${nombre}" ha sido eliminada.`);
    //         } catch (error) {
    //             console.error("Error al eliminar la receta:", error);
    //             mostrarError("Error al eliminar la receta.");
    //         } finally {
    //             setIsConfirmModalOpen(false);
    //             dispatch(hideLoading());
    //         }
    //     }
    // };

    const toggleFavorite = async (event: React.MouseEvent<SVGSVGElement>) => {
        event.stopPropagation();

        dispatch(showLoading());
        try {
            const updatedFavorite = !isFavorite;
            await updateDoc(doc(db, "recetas", id), {
                isFavorite: updatedFavorite,
            });
            setIsFavorite(updatedFavorite);

            // En lugar de recargar toda la lista, actualizamos el estado local desde Recetas
            onFavoriteToggle(id);

            mostrarExito(
                updatedFavorite
                    ? `Receta "${nombre}" añadida a favoritos.`
                    : `Receta "${nombre}" eliminada de favoritos.`
            );
        } catch (error) {
            console.error("Error al actualizar la receta favorita:", error);
            mostrarError("Error al actualizar la receta favorita.");
        } finally {
            dispatch(hideLoading());
        }
    };

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    const disableFullScreen = () => {
        if (isFullScreen) {
            setIsFullScreen(false);
        }
    };

    return (
        <Fragment>
            <div
                className="card mb-4"
                style={{ width: "100%" }}
                onClick={handleShowModal}
            >
                <div className="image-container">
                    <div onClick={handleShowModal} style={{ cursor: "pointer" }}>
                        <ImageCarousel
                            images={imageList}
                            startIndex={currentIndex}
                            onIndexChange={(i) => {
                            setCurrentIndex(i);
                            onIndexChange?.(i); // avisamos a la lista para mantener sincronía
                            }}
                            height={isGridView ? 180 : 250}
                            rounded={false}
                        />
                    </div>
                </div>
                <div className="card-body">
                    <h5 className="card-title text-center">{nombre}</h5>
                    {!isGridView && (
                        <div className="row">
                            <div className="col d-flex align-items-end" style={{ minHeight: "100%" }}>
                                <div className="w-100 text-start">
                                    <span
                                        className="badge text-white"
                                        style={{
                                            backgroundColor: getBadgeColor(categoria),
                                            fontSize: "0.85rem",
                                            padding: "6px 10px",
                                            borderRadius: "12px"
                                        }}
                                    >
                                        {categoria}
                                    </span>
                                </div>
                            </div>

                            <div className="col-4 text-end mt-2">
                                {isEditing && (
                                    <FontAwesomeIcon
                                        title="Editar"
                                        icon={faPenToSquare}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEditarReceta({
                                                id,
                                                nombre,
                                                imagenUrl,
                                                categoria,
                                                ingredientes,
                                                apuntes,
                                                isFavorite
                                            });
                                        }}
                                        style={{
                                            fontSize: "1.2rem",
                                            verticalAlign: "middle",
                                            color: "#495057",
                                            marginRight: "12px",
                                            cursor: "pointer",
                                            transition: "color 0.2s ease, transform 0.2s ease"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = "scale(1.15)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = "scale(1)";
                                        }}
                                    />
                                )}

                                <FontAwesomeIcon
                                    title={
                                        isFavorite
                                            ? "Quitar de favoritos"
                                            : "Añadir a favoritos"
                                    }
                                    icon={
                                        isFavorite
                                            ? faHeartSolid
                                            : faHeartRegular
                                    }
                                    onClick={(e) => toggleFavorite(e)}
                                    className={`heart-icon ${isFavorite ? "latir" : ""}`}
                                    style={{
                                        fontSize: "1.2rem",
                                        verticalAlign: "middle",
                                        color: "red",
                                        cursor: "pointer"
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* {isConfirmModalOpen && (
                <Modal onClose={cancelDeleteReceta}>
                    <div className="modal-body">
                        <h2>Confirmar Eliminación</h2>
                        <p>¿Estás seguro de que deseas eliminar esta receta?</p>
                        <p>Esta acción no se puede deshacer.</p>
                        <div className="row">
                        <div className="col-6" style={{ paddingLeft: 0 }}>
                                <button
                                    className="btn btn-secondary btn-block"
                                    onClick={cancelDeleteReceta}
                                >
                                    Cancelar
                                </button>
                            </div>
                            <div className="col-6" style={{ paddingRight: 0 }}>
                            <button
                                    className="btn btn-danger btn-block"
                                    onClick={confirmDeleteReceta}
                                >
                                    Eliminar
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal>
            )} */}

            {isModalOpen && (
                <Modal onClose={handleCloseModal}>
                    <div className="modal-body modal-content" style={{ padding: 0 }}>
                        <div className="image-container">
                            <img
                                src={imagenUrl}
                                className="img-fluid fixed-image"
                                alt={nombre}
                                onClick={disableFullScreen}
                                onDoubleClick={toggleFullScreen}
                                style={{
                                    width: isFullScreen ? "100vw" : "100%",
                                    height: isFullScreen ? "100vh" : "280px",
                                    objectFit: isFullScreen
                                        ? "contain"
                                        : "cover",
                                    position: isFullScreen
                                        ? "fixed"
                                        : "relative",
                                    top: isFullScreen ? "0" : "",
                                    left: isFullScreen ? "0" : "",
                                    zIndex: isFullScreen ? 1000 : "",
                                    cursor: "pointer",
                                    overflow: "hidden",
                                }}
                            />
                        </div>
                        <h3 className="text-center pt-3 mb-1">
                            <strong>{nombre}</strong>
                        </h3>

                        <p className="text-center">
                            <span
                                className="badge text-white"
                                style={{
                                    backgroundColor: getBadgeColor(categoria),
                                    fontSize: "0.85rem",
                                    borderRadius: "12px"
                                }}
                            >
                                {categoria}
                            </span>
                        </p>

                        <div className={`${apuntes ? "mb-4" : ""}`}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>Ingredientes:</strong>
                                <FontAwesomeIcon
                                    icon={ingredientesEnColumna ? faList : faThLarge}
                                    title={ingredientesEnColumna ? "Ver en filas" : "Ver en columnas"}
                                    style={{
                                        cursor: "pointer",
                                        fontSize: "1.1rem",
                                        color: "#666",
                                    }}
                                    onClick={() => setIngredientesEnColumna(prev => !prev)}
                                />
                            </div>
                            <ul className={!ingredientesEnColumna ? "ingredientes-lista ps-2 m-0" : "ingredientes-lista d-flex flex-wrap ps-2 m-0"}
                                style={!ingredientesEnColumna ? {
                                            display: "grid",
                                            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                            gap: "0.25rem 1.5rem",
                                            paddingLeft: "1rem",
                                            listStyleType: "disc",
                                        } : {}
                                }
                            >
                                {ingredientes.map((ingrediente, index) => (
                                    <li key={index} className={!ingredientesEnColumna ? "" : "col-6"}>
                                        {ingrediente}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        {apuntes && (
                            <>
                                <p className="mb-2">
                                    <strong>Notas:</strong>
                                </p>
                                <p>{apuntes}</p>
                            </>
                        )}
                    </div>
                </Modal>
            )}
        </Fragment>
    );
};

export default CartaReceta;
